import { useEffect, useState, type FormEvent } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, api, type AdminRole, type AdminUser } from "../api.js";
import { useAuth, useToken } from "../auth.js";

export function Users() {
  const token = useToken();
  const { user: current } = useAuth();
  const { t, i18n } = useLingui();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setError(null);
    api
      .listUsers(token)
      .then((res) => setUsers(res.users))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  };
  useEffect(load, [token]);

  const toggleDisabled = async (u: AdminUser) => {
    if (u.id === current?.id && !u.disabledAt) return; // can't disable self
    setBusyId(u.id);
    try {
      await api.setUserDisabled(token, u.id, !u.disabledAt);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (u: AdminUser) => {
    const next = window.prompt(t`New password for ${u.email}:`);
    if (!next) return;
    if (next.length < 8) {
      window.alert(t`Password must be at least 8 characters.`);
      return;
    }
    setBusyId(u.id);
    try {
      await api.updateUserPassword(token, u.id, next);
      window.alert(t`Password updated.`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t`Failed: ${msg}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Administrators & resellers</Trans></h1>
        <button onClick={() => setCreating(true)}><Trans>New account</Trans></button>
      </header>
      {error && <div className="banner err">{error}</div>}
      {!users ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th><Trans>Email</Trans></th>
              <th><Trans>Name</Trans></th>
              <th><Trans>Role</Trans></th>
              <th><Trans>Last sign-in</Trans></th>
              <th><Trans>Status</Trans></th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.email}{current?.id === u.id ? t` (you)` : ""}</td>
                <td>{u.name ?? "—"}</td>
                <td>
                  {u.role === "reseller"
                    ? <span className="badge"><Trans>Reseller</Trans></span>
                    : <span className="badge"><Trans>Superuser</Trans></span>}
                </td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(i18n.locale) : t`never`}</td>
                <td>{u.disabledAt ? <span className="badge badge-err"><Trans>disabled</Trans></span> : <span className="badge badge-ok"><Trans>active</Trans></span>}</td>
                <td className="row-actions">
                  <button
                    className="ghost"
                    disabled={busyId === u.id}
                    onClick={() => void resetPassword(u)}
                  >
                    <Trans>Reset password</Trans>
                  </button>
                  <button
                    className="ghost"
                    disabled={busyId === u.id || (u.id === current?.id && !u.disabledAt)}
                    onClick={() => void toggleDisabled(u)}
                  >
                    {u.disabledAt ? <Trans>Re-enable</Trans> : <Trans>Disable</Trans>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <NewUserDialog
          token={token}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function NewUserDialog({
  token,
  onCancel,
  onCreated,
}: {
  token: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { t } = useLingui();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<AdminRole>("superuser");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr(t`Password must be at least 8 characters.`);
      return;
    }
    setBusy(true);
    try {
      await api.createUser(token, {
        email,
        password,
        name: name.trim() || undefined,
        role,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2><Trans>New account</Trans></h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Role</Trans>
            <select value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
              <option value="superuser">{t`Superuser (Jalfa employee — full access)`}</option>
              <option value="reseller">{t`Reseller (scoped to their own customers)`}</option>
            </select>
          </label>
          <label>
            <Trans>Email</Trans>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <Trans>Name</Trans>
            <input
              type="text"
              placeholder={t`optional`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            <Trans>Password</Trans>
            <input
              type="password"
              placeholder={t`min 8 characters`}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {err && <div className="banner err">{err}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={busy}>
              {busy ? <Trans>Creating…</Trans> : <Trans>Create</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
